/**
 * Derive the State/Region + Country defaults used to pre-seed a NEW MANUAL
 * address, so the user rarely retypes them.
 *
 * Source of truth is the workspace's dedicated "manual default region" fields
 * (manual_default_state / manual_default_country), which the Settings page fills
 * from a region-level Google autocomplete (long names, e.g. "Texas",
 * "United States"). Whatever we produce is normalized to the closed-list display
 * values (COUNTRIES / US_STATES) so the manual editor's dropdowns accept it.
 */
import { COUNTRIES, US_STATES, normalizeCountryCode } from "../constants/geoLists";

export interface ManualDefaults {
  state: string;
  country: string;
}

/** ISO-2 (the codes ConnectYid routes) → the COUNTRIES list display name. */
const CODE_TO_COUNTRY: Record<string, string> = {
  US: "United States",
  CA: "Canada",
  AU: "Australia",
  GB: "United Kingdom",
  IL: "Israel",
};

/** US state abbreviation → full name (US is the only state list keyed by abbrev). */
const US_STATE_ABBREV: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", DC: "District of Columbia",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota",
  MS: "Mississippi", MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada",
  NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York",
  NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon",
  PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
  WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

/** Map any country string (name or code) to a COUNTRIES display name, or "" if unknown. */
const toCountryName = (raw: string): string => {
  const c = raw.trim();
  if (!c) return "";
  if (COUNTRIES.includes(c)) return c;
  return CODE_TO_COUNTRY[normalizeCountryCode(c)] ?? c;
};

/** Map a state string (full name or US abbrev) to a US_STATES display name where possible. */
const toStateName = (country: string, raw: string): string => {
  const s = raw.trim();
  if (!s) return "";
  if (US_STATES.includes(s)) return s;
  if (normalizeCountryCode(country) === "US") {
    return US_STATE_ABBREV[s.toUpperCase()] ?? s;
  }
  return s;
};

/**
 * Returns the State / Country to seed into a new manual address, or empty strings
 * when the workspace has not configured a manual-default region.
 */
export const deriveManualDefaults = (workspace: any): ManualDefaults => {
  const country = toCountryName((workspace?.manual_default_country || "").trim());
  const state = toStateName(country, (workspace?.manual_default_state || "").trim());
  return { state, country };
};
